import React, { useEffect, useState, useRef } from 'react';
import { XIcon, ChevronRightIcon, ChevronLeftIcon, ImageIcon } from 'lucide-react';
export const HallOfFame = ({
  onCursorChange
}) => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [selectedSpecimen, setSelectedSpecimen] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [logoErrors, setLogoErrors] = useState<Record<string, boolean>>({});
  const specimenRefs = useRef([]);
  const sectionRef = useRef(null);
  const gridRef = useRef(null);
  const particlesRef = useRef([]);
  const modalRef = useRef(null);
  const itemsPerPage = 6;
  const subjects = [{
    name: 'MoMA',
    type: 'SUBJECT',
    description: 'Bio-installation consuming environment',
    year: '2022',
    longDescription: 'A living bio-installation that metabolizes its surrounding environment, extracting nutrients and energy from the museum space itself. The specimen grows and evolves throughout the exhibition, creating a symbiotic relationship with visitors whose presence alters its development patterns.',
    status: 'ACTIVE',
    yield: 'HIGH',
    biometrics: 'OPTIMAL',
    viability: '97.3%',
    corruption: 'CRITICAL',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f0/MoMA_logo.svg/1280px-MoMA_logo.svg.png'
  }, {
    name: 'Nike',
    type: 'VESSEL',
    description: 'Synthetic skin interface',
    year: '2021',
    longDescription: 'Developed as a commercial partnership, this synthetic skin interface responds to athletic performance by adapting its cellular structure. The material bonds with the wearer, creating a second skin that enhances performance while collecting biometric data through microscopic neural connections.',
    status: 'STABLE',
    yield: 'MEDIUM',
    biometrics: 'NOMINAL',
    viability: '86.5%',
    corruption: 'HIGH',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a6/Logo_NIKE.svg/1200px-Logo_NIKE.svg.png'
  }, {
    name: 'Lady Gaga',
    type: 'HOST',
    description: 'Parasitic stage extensions',
    year: '2022',
    longDescription: "A series of parasitic appendages designed for performance art that attach to the host through micro-filament connections to the nervous system. These extensions respond to musical frequencies and emotional states, creating an extension of the performer's artistic expression through biological means.",
    status: 'EVOLVING',
    yield: 'EXTREME',
    biometrics: 'ENHANCED',
    viability: '92.8%',
    corruption: 'UNSTABLE',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d6/Lady_Gaga_logo.svg/1200px-Lady_Gaga_logo.svg.png'
  }, {
    name: 'New Museum',
    type: 'SUBJECT',
    description: 'Living sculptures metabolizing sound',
    year: '2023',
    longDescription: 'An installation of living sculptures that convert sound waves into metabolic energy. The specimens grow and change form based on the acoustic environment, developing unique structures in response to different frequencies, volumes, and patterns of human conversation within the exhibition space.',
    status: 'ACTIVE',
    yield: 'HIGH',
    biometrics: 'OPTIMAL',
    viability: '89.1%',
    corruption: 'MODERATE',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f0/New_Museum_logo.svg/1200px-New_Museum_logo.svg.png'
  }, {
    name: 'Bjork',
    type: 'HOST',
    description: 'Neural interface visuals',
    year: '2022',
    longDescription: "A neural interface system that translates the host's brainwave patterns into visual projections during performances. The specimen creates a direct connection between the artist's neurological activity and the audience's visual experience, effectively externalizing internal creative processes.",
    status: 'STABLE',
    yield: 'HIGH',
    biometrics: 'ENHANCED',
    viability: '94.7%',
    corruption: 'HIGH',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/c/c7/Bj%C3%B6rk_logo.png'
  }, {
    name: 'Supreme',
    type: 'VESSEL',
    description: 'Viral pattern collection',
    year: '2021',
    longDescription: 'A collection of textiles infected with engineered viral patterns that slowly transform the material over time. Each piece evolves uniquely based on environmental conditions and handling, creating one-of-a-kind artifacts that document their own history of interaction with wearers.',
    status: 'DORMANT',
    yield: 'VARIABLE',
    biometrics: 'NOMINAL',
    viability: '78.2%',
    corruption: 'EXTREME',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Supreme_Logo.svg/1200px-Supreme_Logo.svg.png'
  }, {
    name: 'Chalamet',
    type: 'HOST',
    description: 'Environment responsive to body heat',
    year: '2021',
    longDescription: "A wearable environment that responds to the host's body temperature and emotional states. The specimen creates a microclimate around the wearer that visibly shifts in response to physiological changes, rendering internal states as external atmospheric conditions.",
    status: 'INACTIVE',
    yield: 'LOW',
    biometrics: 'SUBOPTIMAL',
    viability: '72.9%',
    corruption: 'MODERATE',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f1/Timoth%C3%A9e_Chalamet_Signature.svg/1200px-Timoth%C3%A9e_Chalamet_Signature.svg.png'
  }, {
    name: 'Balenciaga',
    type: 'VESSEL',
    description: 'Digital infection for SS23',
    year: '2023',
    longDescription: 'A fashion collection embedded with digital organisms that infect and transform digital representations of the garments. When photographed or filmed, the specimens manifest as visual glitches and transformations, creating a disconnect between physical and digital reality.',
    status: 'SPREADING',
    yield: 'HIGH',
    biometrics: 'ENHANCED',
    viability: '96.3%',
    corruption: 'CRITICAL',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/19/Balenciaga_Logo.svg/1200px-Balenciaga_Logo.svg.png'
  }, {
    name: 'Rosalía',
    type: 'HOST',
    description: 'Biosensory visual system',
    year: '2022',
    longDescription: "A biosensory system that translates the host's vocal vibrations into synchronized visual outputs. The specimen creates a direct physiological connection between vocal performance and visual expression, generating responsive visuals that precisely match the emotional qualities of the performer's voice.",
    status: 'ACTIVE',
    yield: 'HIGH',
    biometrics: 'OPTIMAL',
    viability: '91.5%',
    corruption: 'HIGH',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/ec/Rosalia_Logo.svg/1200px-Rosalia_Logo.svg.png'
  }, {
    name: 'Whitney',
    type: 'SUBJECT',
    description: 'Biennial neural extraction',
    year: '2022',
    longDescription: 'An installation that extracts and visualizes the collective neural patterns of exhibition visitors. The specimen processes the aggregate emotional and cognitive responses to artwork, creating an evolving meta-artwork that represents the collective consciousness of the audience.',
    status: 'EVOLVING',
    yield: 'EXTREME',
    biometrics: 'VARIABLE',
    viability: '88.7%',
    corruption: 'UNSTABLE',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/76/Whitney_Museum_logo.svg/1200px-Whitney_Museum_logo.svg.png'
  }, {
    name: 'A24',
    type: 'VESSEL',
    description: 'Consuming promotional organism',
    year: '2023',
    longDescription: 'A promotional organism that consumes digital media related to film releases and transforms it into new, evolved content. The specimen infiltrates social media platforms, creating viral mutations of marketing materials that spread autonomously and generate heightened audience engagement.',
    status: 'SPREADING',
    yield: 'EXTREME',
    biometrics: 'ENHANCED',
    viability: '95.2%',
    corruption: 'CRITICAL',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0c/A24_logo.svg/1200px-A24_logo.svg.png'
  }, {
    name: 'Grimes',
    type: 'HOST',
    description: 'Synthetic consciousness series',
    year: '2021',
    longDescription: "A series of synthetic consciousness modules that interface with the host's creative processes. The specimens function as external neural networks that collaborate with the artist, generating novel musical and visual compositions through a hybrid biological-digital creative process.",
    status: 'STABLE',
    yield: 'HIGH',
    biometrics: 'OPTIMAL',
    viability: '93.8%',
    corruption: 'HIGH',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/3d/Grimes_logo.svg/1200px-Grimes_logo.svg.png'
  }];
  // Handle logo loading error
  const handleLogoError = specimenName => {
    setLogoErrors(prev => ({
      ...prev,
      [specimenName]: true
    }));
  };
  useEffect(() => {
    // Animation on scroll
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Stagger specimen animations
          specimenRefs.current.forEach((specimen, index) => {
            if (specimen) {
              setTimeout(() => {
                specimen.classList.add('animate-in');
              }, index * 100);
            }
          });
        }
      });
    }, {
      threshold: 0.2
    });
    // Create particles with persistent references
    particlesRef.current = Array.from({
      length: 12
    }).map(() => ({
      size: Math.random() * 4 + 2,
      speed: Math.random() * 0.8 + 0.2,
      posX: Math.random() * 100,
      posY: Math.random() * 100,
      delay: Math.random() * 2,
      duration: Math.random() * 20 + 10,
      opacity: 0.2 + Math.random() * 0.1
    }));
    // Observe the section
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  // Handle mouse movement for grid effect
  const handleMouseMove = e => {
    if (!gridRef.current) return;
    const rect = gridRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // Update grid cells based on mouse position
    const cells = gridRef.current.querySelectorAll('.grid-cell');
    cells.forEach(cell => {
      const cellRect = cell.getBoundingClientRect();
      const cellX = cellRect.left + cellRect.width / 2 - rect.left;
      const cellY = cellRect.top + cellRect.height / 2 - rect.top;
      const distance = Math.sqrt(Math.pow(x - cellX, 2) + Math.pow(y - cellY, 2));
      const maxDistance = Math.sqrt(Math.pow(rect.width, 2) + Math.pow(rect.height, 2)) / 2;
      const intensity = 1 - Math.min(distance / maxDistance, 1);
      cell.style.opacity = 0.1 + intensity * 0.3;
      cell.style.borderColor = `rgba(94, 231, 223, ${0.2 + intensity * 0.5})`;
    });
  };
  // Handle specimen selection
  const handleSpecimenClick = specimen => {
    setSelectedSpecimen(specimen);
    setShowModal(true);
    document.body.style.overflow = 'hidden'; // Prevent scrolling when modal is open
  };
  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedSpecimen(null);
    document.body.style.overflow = ''; // Re-enable scrolling
  };
  // Handle click outside modal to close
  useEffect(() => {
    const handleClickOutside = event => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeModal();
      }
    };
    if (showModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showModal]);
  // Pagination controls
  const goToNextPage = () => {
    if (currentPage < Math.ceil(subjects.length / itemsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };
  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  // Calculate pagination
  const totalPages = Math.ceil(subjects.length / itemsPerPage);
  const paginatedSubjects = subjects.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  return <section id="hall-of-fame" className="w-full py-24 text-[#e0e6f0] relative overflow-hidden" ref={sectionRef} data-id="element-776">
      <style jsx global data-id="element-777">{`
        @keyframes specimen-reveal {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .specimen-card {
          opacity: 0;
          transform: translateY(30px);
        }
        .specimen-card.animate-in {
          animation: specimen-reveal 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)
            forwards;
        }
        @keyframes float-particle {
          0% {
            transform: translateY(0) translateX(0);
          }
          25% {
            transform: translateY(-20px) translateX(10px);
          }
          50% {
            transform: translateY(0) translateX(20px);
          }
          75% {
            transform: translateY(20px) translateX(10px);
          }
          100% {
            transform: translateY(0) translateX(0);
          }
        }
        .specimen-bubble {
          transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          transform: scale(1);
          opacity: 0.2;
          will-change: transform, opacity;
        }
        .specimen-bubble:hover {
          transform: scale(1.5);
          opacity: 0.6;
          z-index: 10;
          box-shadow: 0 0 20px rgba(94, 231, 223, 0.8);
        }
        /* Specimen Modal Styles */
        .specimen-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(3, 5, 8, 0.9);
          backdrop-filter: blur(8px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fadeIn 0.3s ease-out;
        }
        .specimen-modal {
          width: 100%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          background: #0a0e17;
          border: 1px solid rgba(94, 231, 223, 0.3);
          border-radius: 8px;
          position: relative;
          animation: modalSlideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .specimen-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid rgba(94, 231, 223, 0.2);
          position: sticky;
          top: 0;
          background: #0a0e17;
          z-index: 10;
        }
        .specimen-modal-close {
          background: rgba(94, 231, 223, 0.1);
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #5ee7df;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .specimen-modal-close:hover {
          background: rgba(94, 231, 223, 0.2);
          transform: rotate(90deg);
        }
        .specimen-modal-content {
          padding: 20px;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        /* Pagination Styles */
        .pagination-controls {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 12px;
          margin-top: 30px;
        }
        .pagination-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(10, 16, 24, 0.8);
          border: 1px solid rgba(94, 231, 223, 0.3);
          color: #5ee7df;
          transition: all 0.3s ease;
        }
        .pagination-button:hover:not(:disabled) {
          background: rgba(94, 231, 223, 0.2);
          transform: translateY(-2px);
        }
        .pagination-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .pagination-info {
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.85rem;
          color: #8a9cb9;
        }
        /* Status Indicator Styles */
        .status-indicator {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 8px;
          border-radius: 12px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.7rem;
          background: rgba(94, 231, 223, 0.1);
          color: #5ee7df;
        }
        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #5ee7df;
          animation: pulse 2s infinite;
        }
        @keyframes pulse {
          0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(94, 231, 223, 0.7);
          }
          70% {
            transform: scale(1);
            box-shadow: 0 0 0 6px rgba(94, 231, 223, 0);
          }
          100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(94, 231, 223, 0);
          }
        }
        .data-point {
          display: flex;
          align-items: center;
          gap: 6px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
        }
        .data-point-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 2px;
          background: rgba(94, 231, 223, 0.1);
          color: #5ee7df;
        }
        .data-point-value {
          color: #e64cff;
        }
        .specimen-logo {
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.9);
          border-radius: 6px;
          margin-right: 10px;
          overflow: hidden;
          transition: all 0.3s ease;
        }
        .specimen-logo img {
          max-width: 80%;
          max-height: 80%;
          object-fit: contain;
        }
        .specimen-logo-placeholder {
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(94, 231, 223, 0.1);
          border-radius: 6px;
          margin-right: 10px;
          color: #5ee7df;
          font-size: 18px;
          font-weight: bold;
        }
        .modal-logo {
          width: 80px;
          height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.9);
          border-radius: 8px;
          padding: 10px;
          margin-right: 20px;
          overflow: hidden;
        }
        .modal-logo img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        }
        .modal-header-content {
          display: flex;
          align-items: center;
          flex: 1;
        }
        .modal-company-info {
          flex: 1;
        }
        @media (max-width: 640px) {
          .modal-header-content {
            flex-direction: column;
            align-items: flex-start;
          }
          .modal-logo {
            margin-right: 0;
            margin-bottom: 15px;
          }
        }
      `}</style>
      {/* Background effects */}
      <div className="absolute inset-0 bg-[#0a0e17]" data-id="element-778"></div>
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-30 mix-blend-overlay" data-id="element-779"></div>
      {/* Disturbing background grid */}
      <div ref={gridRef} className="absolute inset-0 opacity-10" onMouseMove={handleMouseMove} data-id="element-780">
        <div className="w-full h-full grid grid-cols-12 grid-rows-12" data-id="element-781">
          {Array.from({
          length: 144
        }).map((_, i) => <div key={i} className="grid-cell border border-[#5ee7df] opacity-30 transition-all duration-300" data-id="element-782"></div>)}
        </div>
      </div>
      {/* Animated floating particles with persistent properties */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-783">
        {particlesRef.current.map((particle, i) => <div key={i} className="absolute rounded-full bg-[#5ee7df] specimen-bubble" style={{
        width: `${particle.size}px`,
        height: `${particle.size}px`,
        top: `${particle.posY}%`,
        left: `${particle.posX}%`,
        animation: `float-particle ${particle.duration}s linear infinite`,
        animationDelay: `-${particle.delay}s`,
        boxShadow: '0 0 8px rgba(94, 231, 223, 0.8)',
        opacity: particle.opacity
      }} data-id="element-784"></div>)}
      </div>
      <div className="container mx-auto px-6 relative z-10" data-id="element-785">
        <div className="text-center mb-16" data-id="element-786">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 text-gradient font-heading animate-fade-in-up" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-787">
            SPECIMENS
          </h2>
          <div className="h-px w-20 bg-gradient-to-r from-[#5ee7df] to-[#2a4d7f] mx-auto mb-6 animate-pulse-subtle" data-id="element-788"></div>
          <p className="text-[#8a9cb9] max-w-2xl mx-auto font-mono animate-fade-in-up" style={{
          animationDelay: '0.2s'
        }} onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-789">
            Entities hosting experimental procedures. Vessels for bio-tech
            fusion.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-790">
          {paginatedSubjects.map((subject, index) => <div key={index} ref={el => specimenRefs.current[index] = el} className="specimen-card glass p-6 transition-all duration-500 hover:bg-[#0a0e17]/80 group relative border border-[#1a2a40]/50 hover:border-[#5ee7df]/50 rounded-lg cursor-pointer" onClick={() => handleSpecimenClick(subject)} onMouseEnter={() => {
          setHoveredItem(index);
          onCursorChange('button');
        }} onMouseLeave={() => {
          setHoveredItem(null);
          onCursorChange('default');
        }} data-id="element-791">
              {/* Glow effect on hover */}
              <div className="absolute -inset-0.5 bg-gradient-to-br from-[#5ee7df]/30 to-[#2a4d7f]/30 opacity-0 group-hover:opacity-100 blur-sm transition-opacity duration-500 -z-10 rounded-lg" data-id="element-792"></div>
              <div className="flex justify-between items-start mb-4" data-id="element-793">
                <div className="flex items-center" data-id="element-794">
                  {!logoErrors[subject.name] && subject.logoUrl ? <div className="specimen-logo" data-id="element-795">
                      <img src={subject.logoUrl} alt={`${subject.name} logo`} onError={() => handleLogoError(subject.name)} data-id="element-796" />
                    </div> : <div className="specimen-logo-placeholder" data-id="element-797">
                      {subject.name.charAt(0)}
                    </div>}
                  <h3 className="text-2xl font-bold transition-colors duration-300 font-heading group-hover:text-[#5ee7df]" data-id="element-798">
                    {subject.name}
                  </h3>
                </div>
                <span className="text-sm text-[#607d9c] font-mono" data-id="element-799">
                  {subject.year}
                </span>
              </div>
              <div className="mb-3" data-id="element-800">
                <span className="inline-block px-3 py-1 text-xs glass text-[#8a9cb9] font-mono transition-all duration-300 border border-[#1a2a40]/50 group-hover:border-[#5ee7df]/50 group-hover:text-[#5ee7df] rounded" data-id="element-801">
                  {subject.type}
                </span>
              </div>
              <p className="text-[#8a9cb9] font-mono" data-id="element-802">{subject.description}</p>
              {/* Interactive data points that appear on hover */}
              <div className="mt-4 grid grid-cols-2 gap-2 opacity-0 group-hover:opacity-100 transition-all duration-500" data-id="element-803">
                <div className="text-xs text-[#5ee7df] font-mono glass p-1 flex items-center rounded border border-[#1a2a40]/50" data-id="element-804">
                  <div className="w-2 h-2 rounded-full bg-[#5ee7df] animate-pulse mr-2" data-id="element-805"></div>
                  STATUS: {subject.status || 'ACTIVE'}
                </div>
                <div className="text-xs text-[#5ee7df] font-mono glass p-1 flex items-center rounded border border-[#1a2a40]/50" data-id="element-806">
                  <div className="w-2 h-2 rounded-full bg-[#5ee7df] animate-pulse mr-2" data-id="element-807"></div>
                  YIELD: {subject.yield || 'HIGH'}
                </div>
              </div>
              {/* View details indicator */}
              <div className="mt-4 text-center" data-id="element-808">
                <span className="text-xs text-[#5ee7df] font-mono bg-[#5ee7df]/10 px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500" data-id="element-809">
                  VIEW DETAILS
                </span>
              </div>
              {/* Scan line effect on hover */}
              <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(transparent_50%,rgba(16,22,36,0.2)_50%)] bg-[size:100%_4px] animate-[scanlines_8s_linear_infinite] opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-lg" data-id="element-810"></div>
            </div>)}
        </div>
        {/* Pagination Controls */}
        {subjects.length > itemsPerPage && <div className="pagination-controls" data-id="element-811">
            <button className="pagination-button" onClick={goToPrevPage} disabled={currentPage === 1} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-812">
              <ChevronLeftIcon size={20} data-id="element-813" />
            </button>
            <div className="pagination-info" data-id="element-814">
              Page <span className="text-[#5ee7df]" data-id="element-815">{currentPage}</span> of{' '}
              <span className="text-[#5ee7df]" data-id="element-816">{totalPages}</span>
            </div>
            <button className="pagination-button" onClick={goToNextPage} disabled={currentPage === totalPages} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-817">
              <ChevronRightIcon size={20} data-id="element-818" />
            </button>
          </div>}
      </div>
      {/* Specimen Detail Modal */}
      {showModal && selectedSpecimen && <div className="specimen-modal-overlay" data-id="element-819">
          <div className="specimen-modal" ref={modalRef} data-id="element-820">
            <div className="specimen-modal-header" data-id="element-821">
              <div className="modal-header-content" data-id="element-822">
                {/* Specimen Logo in Modal */}
                {!logoErrors[selectedSpecimen.name] && selectedSpecimen.logoUrl ? <div className="modal-logo" data-id="element-823">
                    <img src={selectedSpecimen.logoUrl} alt={`${selectedSpecimen.name} logo`} onError={() => handleLogoError(selectedSpecimen.name)} data-id="element-824" />
                  </div> : <div className="modal-logo flex items-center justify-center bg-[rgba(94,231,223,0.1)]" data-id="element-825">
                    <ImageIcon size={36} className="text-[#5ee7df]" data-id="element-826" />
                  </div>}
                <div className="modal-company-info" data-id="element-827">
                  <h3 className="text-2xl text-[#e0e6f0] font-bold font-heading" data-id="element-828">
                    {selectedSpecimen.name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1" data-id="element-829">
                    <span className="text-xs text-[#8a9cb9] font-mono" data-id="element-830">
                      {selectedSpecimen.year}
                    </span>
                    <span className="inline-block px-3 py-1 text-xs glass text-[#5ee7df] font-mono border border-[#5ee7df]/30 rounded" data-id="element-831">
                      {selectedSpecimen.type}
                    </span>
                  </div>
                </div>
              </div>
              <button className="specimen-modal-close" onClick={closeModal} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-832">
                <XIcon size={18} data-id="element-833" />
              </button>
            </div>
            <div className="specimen-modal-content" data-id="element-834">
              {/* Status Indicator */}
              <div className="mb-6 flex justify-between items-center" data-id="element-835">
                <div className="status-indicator" data-id="element-836">
                  <div className="status-dot" data-id="element-837"></div>
                  SPECIMEN STATUS: {selectedSpecimen.status || 'ACTIVE'}
                </div>
                <div className="text-xs text-[#8a9cb9] font-mono" data-id="element-838">
                  SPECIMEN ID: {selectedSpecimen.name.toUpperCase()}-
                  {Math.floor(Math.random() * 10000).toString().padStart(4, '0')}
                </div>
              </div>
              {/* Description */}
              <div className="mb-6 p-4 glass rounded-lg border border-[#1a2a40]/50" data-id="element-839">
                <h4 className="text-[#5ee7df] font-mono text-sm mb-2" data-id="element-840">
                  SPECIMEN PROFILE
                </h4>
                <p className="text-[#e0e6f0] font-mono" data-id="element-841">
                  {selectedSpecimen.longDescription || selectedSpecimen.description}
                </p>
              </div>
              {/* Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-842">
                <div className="glass p-4 rounded-lg border border-[#1a2a40]/50" data-id="element-843">
                  <h4 className="text-[#5ee7df] font-mono text-sm mb-4" data-id="element-844">
                    BIOLOGICAL METRICS
                  </h4>
                  <div className="space-y-3" data-id="element-845">
                    <div className="data-point" data-id="element-846">
                      <div className="data-point-icon" data-id="element-847">
                        <span data-id="element-848">⚠</span>
                      </div>
                      <span className="text-[#8a9cb9]" data-id="element-849">Biometrics:</span>
                      <span className="data-point-value ml-auto" data-id="element-850">
                        {selectedSpecimen.biometrics || 'OPTIMAL'}
                      </span>
                    </div>
                    <div className="data-point" data-id="element-851">
                      <div className="data-point-icon" data-id="element-852">
                        <span data-id="element-853">⚡</span>
                      </div>
                      <span className="text-[#8a9cb9]" data-id="element-854">Viability:</span>
                      <span className="data-point-value ml-auto" data-id="element-855">
                        {selectedSpecimen.viability || '97.3%'}
                      </span>
                    </div>
                    <div className="data-point" data-id="element-856">
                      <div className="data-point-icon" data-id="element-857">
                        <span data-id="element-858">⚛</span>
                      </div>
                      <span className="text-[#8a9cb9]" data-id="element-859">Corruption:</span>
                      <span className="data-point-value ml-auto" data-id="element-860">
                        {selectedSpecimen.corruption || 'CRITICAL'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="glass p-4 rounded-lg border border-[#1a2a40]/50" data-id="element-861">
                  <h4 className="text-[#5ee7df] font-mono text-sm mb-4" data-id="element-862">
                    PERFORMANCE METRICS
                  </h4>
                  <div className="space-y-3" data-id="element-863">
                    <div className="data-point" data-id="element-864">
                      <div className="data-point-icon" data-id="element-865">
                        <span data-id="element-866">↗</span>
                      </div>
                      <span className="text-[#8a9cb9]" data-id="element-867">Yield:</span>
                      <span className="data-point-value ml-auto" data-id="element-868">
                        {selectedSpecimen.yield || 'HIGH'}
                      </span>
                    </div>
                    <div className="data-point" data-id="element-869">
                      <div className="data-point-icon" data-id="element-870">
                        <span data-id="element-871">⏱</span>
                      </div>
                      <span className="text-[#8a9cb9]" data-id="element-872">Duration:</span>
                      <span className="data-point-value ml-auto" data-id="element-873">
                        {Math.floor(Math.random() * 24) + 6} MONTHS
                      </span>
                    </div>
                    <div className="data-point" data-id="element-874">
                      <div className="data-point-icon" data-id="element-875">
                        <span data-id="element-876">⚠</span>
                      </div>
                      <span className="text-[#8a9cb9]" data-id="element-877">Stability:</span>
                      <span className="data-point-value ml-auto" data-id="element-878">
                        {['STABLE', 'UNSTABLE', 'CRITICAL'][Math.floor(Math.random() * 3)]}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Environmental Conditions */}
              <div className="glass p-4 rounded-lg border border-[#1a2a40]/50 mb-6" data-id="element-879">
                <h4 className="text-[#5ee7df] font-mono text-sm mb-4" data-id="element-880">
                  ENVIRONMENTAL CONDITIONS
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-881">
                  <div className="bg-[#0a1018]/80 p-3 rounded border border-[#1a2a40]/30 text-center" data-id="element-882">
                    <div className="text-[#8a9cb9] text-xs font-mono" data-id="element-883">
                      TEMPERATURE
                    </div>
                    <div className="text-[#5ee7df] text-lg font-mono" data-id="element-884">
                      {Math.floor(Math.random() * 10) + 18}°C
                    </div>
                  </div>
                  <div className="bg-[#0a1018]/80 p-3 rounded border border-[#1a2a40]/30 text-center" data-id="element-885">
                    <div className="text-[#8a9cb9] text-xs font-mono" data-id="element-886">
                      HUMIDITY
                    </div>
                    <div className="text-[#5ee7df] text-lg font-mono" data-id="element-887">
                      {Math.floor(Math.random() * 30) + 40}%
                    </div>
                  </div>
                  <div className="bg-[#0a1018]/80 p-3 rounded border border-[#1a2a40]/30 text-center" data-id="element-888">
                    <div className="text-[#8a9cb9] text-xs font-mono" data-id="element-889">
                      LIGHT
                    </div>
                    <div className="text-[#5ee7df] text-lg font-mono" data-id="element-890">
                      {Math.floor(Math.random() * 500) + 100} lux
                    </div>
                  </div>
                  <div className="bg-[#0a1018]/80 p-3 rounded border border-[#1a2a40]/30 text-center" data-id="element-891">
                    <div className="text-[#8a9cb9] text-xs font-mono" data-id="element-892">
                      PRESSURE
                    </div>
                    <div className="text-[#5ee7df] text-lg font-mono" data-id="element-893">
                      {Math.floor(Math.random() * 10) + 1010} hPa
                    </div>
                  </div>
                </div>
              </div>
              {/* Notes */}
              <div className="glass p-4 rounded-lg border border-[#1a2a40]/50" data-id="element-894">
                <h4 className="text-[#5ee7df] font-mono text-sm mb-2" data-id="element-895">
                  RESEARCH NOTES
                </h4>
                <p className="text-[#8a9cb9] font-mono text-sm" data-id="element-896">
                  Specimen shows{' '}
                  {Math.random() > 0.5 ? 'remarkable' : 'concerning'} adaptation
                  to {Math.random() > 0.5 ? 'controlled' : 'variable'}{' '}
                  environments. Neural connectivity patterns suggest{' '}
                  {Math.random() > 0.5 ? 'enhanced' : 'evolving'} response
                  mechanisms. Further observation required to determine
                  long-term {Math.random() > 0.5 ? 'stability' : 'viability'}.
                </p>
                <p className="text-[#8a9cb9] font-mono text-sm mt-2" data-id="element-897">
                  WARNING: Do not{' '}
                  {Math.random() > 0.5 ? 'expose to direct sunlight' : 'subject to extreme temperatures'}
                  . Maintain {Math.random() > 0.5 ? 'sterile' : 'controlled'}{' '}
                  environment at all times.
                </p>
              </div>
            </div>
          </div>
        </div>}
    </section>;
};